/**
 * NotificationItem — single notification card with avatar, type badge,
 * title/message/time, and action buttons for buddy requests and group joins.
 * The avatar opens the actor's profile; the title carries the row's own action
 * so the card needs no ARIA button role around nested controls.
 */
import { useMemo, type ComponentType, type MouseEvent } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import {
  BellIcon,
  BuddiesIcon,
  CommentIcon,
  HeartIcon,
  PostMenuIcon,
  UserIcon,
} from '../../components/ui/icons';
import type { IconProps } from '../../components/ui/icons';
import type { NotificationItem as NotificationItemType } from '@wlb/shared/types/notification';
import { Avatar } from '../../components/ui/Avatar';
import { formatApiTimestamp } from '../../utils/apiTimestamp';
import wlbBuddyImage from '../../assets/images/wlb-buddy.png';
import { isFolloweeNewPostNotification } from './notificationRows';
import './notifications.css';

interface BadgeConfig {
  bg: string;
  Icon: ComponentType<IconProps>;
}

interface NotificationItemProps {
  notification: NotificationItemType;
  onPress: (notification: NotificationItemType) => void;
  onAccept: (id: string, senderId?: number) => void;
  onReject: (id: string, senderId?: number) => void;
  onJoin: (notification: NotificationItemType) => void;
  onView: (notification: NotificationItemType) => void;
  onOpenProfile: (notification: NotificationItemType) => void;
  onMenu: (notification: NotificationItemType, anchorRect: DOMRect) => void;
  selectionMode?: boolean;
  selected?: boolean;
  onToggleSelected?: (notification: NotificationItemType) => void;
  menuOpen?: boolean;
}

export function NotificationItem({
  notification,
  onPress,
  onAccept,
  onReject,
  onJoin,
  onView,
  onOpenProfile,
  onMenu,
  selectionMode = false,
  selected = false,
  onToggleSelected,
  menuOpen = false,
}: NotificationItemProps) {
  const { t } = useTranslation();
  const { type, title, message, isRead, user, status, id, senderId } = notification;
  // Same formatter the feed and comments use, so a like and a post from the
  // same moment read the same.
  const time = formatApiTimestamp(notification.createdAt, {
    fallback: notification.time,
    naiveZone: 'local',
  });

  const badge = useMemo<BadgeConfig>(() => {
    switch (type) {
      case 'buddyRequest':
        return { bg: 'var(--color-secondary)', Icon: UserIcon };
      case 'like':
        return { bg: 'var(--color-error)', Icon: HeartIcon };
      case 'comment':
        return { bg: 'var(--color-success)', Icon: CommentIcon };
      case 'groupJoinRequest':
      case 'groupPost':
        return { bg: 'var(--color-accent)', Icon: BuddiesIcon };
      default:
        return { bg: 'var(--color-primary)', Icon: BellIcon };
    }
  }, [type]);

  const isActioned = Boolean(status);
  const avatarUrl = typeof user?.avatar === 'object' ? user.avatar?.uri : user?.avatar;
  const showWlbBuddyAvatar = isFolloweeNewPostNotification(notification);

  // Message-type payloads carry only the chat text, so the resolved actor name
  // is prefixed unless the message already mentions them ("stuee added you…").
  const actorName = user?.name?.trim() ?? '';
  const displayMessage =
    actorName && message && !message.toLowerCase().includes(actorName.toLowerCase())
      ? `${actorName}: ${message}`
      : message || actorName;

  // Post notifications can resolve the actor lazily from GetPost when the
  // notification payload omits the profile GUID.
  const canOpenProfile = !selectionMode && Boolean(
    notification.actorUserGuid || notification.postId,
  );
  const showBuddyActions = !selectionMode && type === 'buddyRequest' && !status;
  const showGroupActions = !selectionMode && type === 'groupJoinRequest' && !status;
  const cardClickable = selectionMode || (!showBuddyActions && !showGroupActions);

  const handleMenu = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onMenu(notification, event.currentTarget.getBoundingClientRect());
  };

  // Render the bundled brand mark directly. Avatar intentionally resolves API
  // profile sources and may replace failed images with the generic person icon.
  const avatarVisual = showWlbBuddyAvatar ? (
    <span
      className="ui-avatar ui-avatar--md notification-item__avatar notification-item__avatar--wlb-buddy"
      aria-hidden
    >
      <img src={wlbBuddyImage} alt="" aria-hidden />
    </span>
  ) : (
    <Avatar
      src={avatarUrl}
      name={user?.name}
      username={user?.name}
      seed={notification.actorUserGuid ?? senderId}
      size="md"
      className="notification-item__avatar"
    />
  );

  return (
    <article
      className={[
        'notification-item',
        !isRead && !isActioned ? 'notification-item--unread' : '',
        selectionMode ? 'notification-item--selectable' : '',
        selected ? 'notification-item--selected' : '',
        cardClickable ? 'notification-item--clickable' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={
        selectionMode
          ? () => onToggleSelected?.(notification)
          : cardClickable
            ? () => onPress(notification)
            : undefined
      }
    >
      <div className="notification-item__header">
        {selectionMode && (
          <input
            type="checkbox"
            className="notification-item__checkbox"
            checked={selected}
            aria-label={t('home.notifications.accessibility.selectNotification', { title })}
            onClick={event => event.stopPropagation()}
            onChange={() => onToggleSelected?.(notification)}
          />
        )}
        <span className="notification-item__avatar-wrap">
          {canOpenProfile ? (
            <button
              type="button"
              className="notification-item__avatar-button"
              aria-label={t('home.accessibility.openProfile')}
              onClick={event => {
                event.stopPropagation();
                onOpenProfile(notification);
              }}
            >
              {avatarVisual}
            </button>
          ) : (
            avatarVisual
          )}
          <span className="notification-item__badge" style={{ background: badge.bg }} aria-hidden>
            <badge.Icon size={11} />
          </span>
        </span>

        <div className="notification-item__content">
          {cardClickable && !selectionMode ? (
            <button
              type="button"
              className="notification-item__title notification-item__title-button"
              aria-label={`${title}. ${displayMessage}. ${time}`}
              onClick={event => {
                event.stopPropagation();
                onPress(notification);
              }}
            >
              {title}
            </button>
          ) : (
            <p className="notification-item__title">{title}</p>
          )}
          <p className="notification-item__message">{displayMessage}</p>
          <p className="notification-item__time">{time}</p>
        </div>
        {!selectionMode && (
          <button
            type="button"
            className={`notification-item__menu${menuOpen ? ' notification-item__menu--open' : ''}`}
            aria-label={t('home.notifications.accessibility.notificationOptions', { title })}
            aria-haspopup="dialog"
            aria-expanded={menuOpen}
            onClick={handleMenu}
          >
            <PostMenuIcon size={20} />
          </button>
        )}
      </div>

      {showBuddyActions && (
        <div className="notification-item__actions">
          <button
            type="button"
            className="notification-item__action notification-item__action--primary"
            onClick={event => { event.stopPropagation(); onAccept(id, senderId); }}
          >
            {t('home.notifications.buttons.accept')}
          </button>
          <button
            type="button"
            className="notification-item__action notification-item__action--secondary"
            onClick={event => { event.stopPropagation(); onReject(id, senderId); }}
          >
            {t('home.notifications.buttons.reject')}
          </button>
        </div>
      )}

      {showGroupActions && (
        <div className="notification-item__actions">
          <button
            type="button"
            className="notification-item__action notification-item__action--primary"
            onClick={event => { event.stopPropagation(); onJoin(notification); }}
          >
            {t('home.notifications.buttons.approve')}
          </button>
          <button
            type="button"
            className="notification-item__action notification-item__action--secondary"
            onClick={event => { event.stopPropagation(); onView(notification); }}
          >
            {t('home.notifications.buttons.view')}
          </button>
        </div>
      )}
    </article>
  );
}
